import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'lib/dayjs';
import {
  useAddEmployeeLeaveMutation,
  useApproveEmployeeLeaveMutation,
  useGetUnplannedLeavesQuery,
} from 'redux/features/employeeLeaveApi';
import {
  useApproveLogUpdateBulkMutation,
  useBulkProcessDayLogsMutation,
  useGetLogUpdateRequestByUserIdQuery,
} from 'redux/features/timeLogApi';
import Icon from '../../../components/AppIcon';
import AddLeaveModal from '../../holiday-management-employee-view/components/AddLeaveModal';
import DayLogEditModal from '../../dashboard-overview/components/DayLogEditModal';
import PickDateModal from '../../../components/ui/PickDateModal';

const fmtDate = (d) => dayjs.utc(d).format('ddd, DD MMM YYYY');
const rowDates = (row) =>
  row.dates?.length ? row.dates : [dayjs.utc(row.date).format('YYYY-MM-DD')];
const fmtTime = (d) => (d ? dayjs(d).format('hh:mm A') : '--');

/**
 * Items HR must clear before calculating salary:
 *  - pending unplanned leaves (employee had no log): add a leave OR add a log
 *  - pending log update requests: approve OR reject
 */
export const usePendingSalaryItems = ({ userId, month, year }) => {
  const skip = !userId;

  const { data: unplannedData, refetch: refetchUnplanned } = useGetUnplannedLeavesQuery(
    { month, year, userId, limit: 100 },
    { skip, refetchOnMountOrArgChange: true },
  );
  const { data: requestsData, refetch: refetchRequests } = useGetLogUpdateRequestByUserIdQuery(
    { id: userId, month, year, status: 'PENDING' },
    { skip, refetchOnMountOrArgChange: true },
  );

  const pendingUnplanned = useMemo(() => {
    const rows = unplannedData?.data || unplannedData || [];
    return (Array.isArray(rows) ? rows : []).filter((r) => r.status === 'PENDING');
  }, [unplannedData]);

  const pendingRequests = useMemo(() => {
    const rows = requestsData?.data || [];
    return (Array.isArray(rows) ? rows : []).filter((r) => r.status === 'PENDING');
  }, [requestsData]);

  // Re-read from the server and return how many items are still open
  const refresh = useCallback(async () => {
    if (skip) return 0;
    const [unplanned, requests] = await Promise.all([refetchUnplanned(), refetchRequests()]);
    const unplannedRows = unplanned?.data?.data || unplanned?.data || [];
    const requestRows = requests?.data?.data || [];
    const count = (rows) => (Array.isArray(rows) ? rows : []).filter((r) => r.status === 'PENDING');
    return count(unplannedRows).length + count(requestRows).length;
  }, [skip, refetchUnplanned, refetchRequests]);

  return {
    pendingUnplanned,
    pendingRequests,
    total: pendingUnplanned.length + pendingRequests.length,
    refresh,
  };
};

const PendingSalaryItems = ({
  isOpen,
  onClose,
  employee,
  monthlyLogs = [],
  pendingUnplanned,
  pendingRequests,
  month,
  year,
}) => {
  const userId = employee?.id;
  const navigate = useNavigate();

  const [addEmployeeLeave, { isLoading: isAddingLeave }] = useAddEmployeeLeaveMutation();
  const [bulkProcessDayLogs] = useBulkProcessDayLogsMutation();
  const [approveBulk, { isLoading: isProcessing }] = useApproveLogUpdateBulkMutation();

  const [approveLeave, { isLoading: isApprovingLeave }] = useApproveEmployeeLeaveMutation();

  const [leaveRow, setLeaveRow] = useState(null);
  const [logDate, setLogDate] = useState(null);
  const [pickRow, setPickRow] = useState(null);

  // not requested yet -> add leave / add log, requested -> HR approves / rejects it
  const needAction = pendingUnplanned.filter((row) => !row.leave_request);
  const awaitingApproval = pendingUnplanned.filter((row) => !!row.leave_request);

  const total = pendingUnplanned.length + pendingRequests.length;

  const handleAddLeave = async (leaveData) => {
    try {
      await addEmployeeLeave({ ...leaveData, status: 'APPROVED' }).unwrap();
      toast.success('Leave added successfully');
      setLeaveRow(null);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to add leave');
    }
  };

  const handleAddLog = async (payload) => {
    try {
      await bulkProcessDayLogs({ userId, ...payload }).unwrap();
      toast.success('Logs updated successfully');
      setLogDate(null);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update logs');
    }
  };

  const handleApproveLeave = async (leave) => {
    try {
      await approveLeave(leave.id).unwrap();
      toast.success('Leave confirmed');
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to approve leave');
    }
  };

  // Open the full log update request page for this employee; it sends HR back to the salary page after submit
  const openFullDetails = () => {
    const returnTo = `/employee-management/employee-time-logs?id=${userId}&month=${month}&year=${year}&calculate=1`;
    const params = new URLSearchParams({
      employeeId: employee?.employee_id || '',
      month: String(month),
      year: String(year),
      returnTo,
    });
    navigate(`/admin-request-management?${params.toString()}`);
  };

  const handleRequest = async (request, status) => {
    try {
      await approveBulk([
        {
          id: request.id,
          status,
          check_in: request.check_in,
          check_out: request.check_out,
        },
      ]).unwrap();
      toast.success(status === 'APPROVED' ? 'Request approved' : 'Request rejected');
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update request');
    }
  };

  if (!isOpen) return null;

  const logsForDay = (date) => {
    const key = dayjs.utc(date).format('YYYY-MM-DD');
    const day = monthlyLogs.find((l) => dayjs.utc(l.date).format('YYYY-MM-DD') === key);
    return day?.fullDayLogs?.filter((detail) => !detail.tempId) || [];
  };

  return (
    <>
      <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
          <div className="flex items-start justify-between gap-3 border-b border-gray-200 bg-amber-50 p-4">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={20} className="mt-0.5 text-amber-600" />
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  Clear pending items before calculating salary
                </h2>
                <p className="text-xs text-gray-600">
                  {employee?.first_name} {employee?.last_name} · {total} item
                  {total === 1 ? '' : 's'} left. Add a leave if the employee was on leave, add a log
                  if they worked, or approve / reject pending log requests. Salary opens
                  automatically when everything is cleared.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
              aria-label="Close"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          <div className="space-y-5 overflow-y-auto p-4">
            {needAction.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Unplanned leaves - action needed ({needAction.length})
                </p>
                <div className="space-y-2">
                  {needAction.map((row) => {
                    const dates = rowDates(row);
                    return (
                      <div
                        key={row.id}
                        className="flex flex-col gap-2 rounded-md border border-gray-200 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="flex flex-wrap gap-1.5">
                          {dates.map((d) => (
                            <span
                              key={d}
                              className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-800"
                            >
                              {fmtDate(d)}
                            </span>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setLeaveRow(row)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                          >
                            <Icon name="CalendarPlus" size={13} />
                            Add Leave
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              dates.length === 1 ? setLogDate(dates[0]) : setPickRow(row)
                            }
                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                          >
                            <Icon name="Clock" size={13} />
                            Add Log
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {awaitingApproval.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Unplanned leaves - waiting for approval ({awaitingApproval.length})
                </p>
                <div className="space-y-2">
                  {awaitingApproval.map((row) => (
                    <div
                      key={row.id}
                      className="flex flex-col gap-2 rounded-md border border-gray-200 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 text-sm text-gray-800">
                        <div className="flex flex-wrap gap-1.5">
                          {rowDates(row).map((d) => (
                            <span
                              key={d}
                              className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium"
                            >
                              {fmtDate(d)}
                            </span>
                          ))}
                        </div>
                        {row.leave_request?.reason && (
                          <p className="mt-1 text-xs text-gray-500">
                            Reason: {row.leave_request.reason}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={isApprovingLeave}
                          onClick={() => handleApproveLeave(row.leave_request)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <Icon name="Check" size={13} />
                          Confirm
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pendingRequests.length > 0 && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Pending log update requests ({pendingRequests.length})
                  </p>
                  <button
                    type="button"
                    onClick={openFullDetails}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
                  >
                    View full details
                    <Icon name="ExternalLink" size={12} />
                  </button>
                </div>
                <div className="space-y-2">
                  {pendingRequests.map((request) => (
                    <div
                      key={request.id}
                      className="flex flex-col gap-2 rounded-md border border-gray-200 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 text-sm text-gray-800">
                        <span className="font-medium">
                          {fmtDate(request.logDate || request.date)}
                        </span>
                        <span className="mx-2 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600">
                          {request.type}
                        </span>
                        {request.unplanned_leave_id && (
                          <span className="mr-2 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                            From unplanned leave
                          </span>
                        )}
                        {request.type !== 'DELETE' && (
                          <span className="text-gray-600">
                            {fmtTime(request.check_in)} – {fmtTime(request.check_out)}
                          </span>
                        )}
                        {request.reason && (
                          <p className="truncate text-xs text-gray-500" title={request.reason}>
                            {request.reason}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={isProcessing}
                          onClick={() => handleRequest(request, 'APPROVED')}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <Icon name="Check" size={13} />
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={isProcessing}
                          onClick={() => handleRequest(request, 'REJECTED')}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                        >
                          <Icon name="X" size={13} />
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end border-t border-gray-200 p-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Sub-modals must sit above the pending modal (which covers the header and sidebar) */}
      <div className="relative z-[9999]">
        {leaveRow && (
          <AddLeaveModal
            isOpen
            onClose={() => setLeaveRow(null)}
            onAddLeave={handleAddLeave}
            isAdminMode
            isSubmitting={isAddingLeave}
            selectedEmployeeId={userId}
            lockedDates={rowDates(leaveRow)}
            employees={[employee]}
          />
        )}

        <PickDateModal
          isOpen={!!pickRow}
          onClose={() => setPickRow(null)}
          dates={pickRow ? rowDates(pickRow) : []}
          title="Which day did the employee work?"
          onSelect={(d) => {
            setPickRow(null);
            setLogDate(d);
          }}
        />

        {logDate && (
          <DayLogEditModal
            isOpen
            onClose={() => setLogDate(null)}
            date={logDate}
            logs={logsForDay(logDate)}
            onSubmit={handleAddLog}
            targetUserId={userId}
          />
        )}
      </div>
    </>
  );
};

export default PendingSalaryItems;
